/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pill, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getTopPrescribed } from '@/lib/rxTemplates';
import { THEME_CONFIG } from '@/config/themeConfig';

/**
 * RxStatsChart — horizontal bar chart of the doctor's top prescribed drugs
 * (by active principle when available, fallback to commercial name).
 */
export const RxStatsChart = ({ days = 30, limit = 10 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(days);

  const load = useCallback(async (d = windowDays) => {
    setLoading(true);
    try {
      const res = await getTopPrescribed({ days: d, limit });
      setData(res);
    } catch (e) {
      toast.error('Impossibile caricare statistiche prescrizioni');
    } finally {
      setLoading(false);
    }
  }, [windowDays, limit]);

  useEffect(() => { load(windowDays); }, [windowDays, load]);

  const chartData = useMemo(() => (data?.items || []).map((it, i) => ({
    ...it,
    label: `${i + 1}. ${it.name}`,
  })), [data]);

  const primary = `var(--wl-primary, ${THEME_CONFIG.colors.primary})`;

  return (
    <Card data-testid="rx-stats-chart">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Pill className="h-5 w-5 text-primary" /> Farmaci più prescritti
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Aggregato per principio attivo. Ultima finestra: {windowDays} giorni.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            data-testid="rx-stats-window-select"
          >
            <option value={7}>7 giorni</option>
            <option value={30}>30 giorni</option>
            <option value={90}>90 giorni</option>
            <option value={365}>1 anno</option>
          </select>
          <Button variant="outline" size="icon" onClick={() => load(windowDays)} aria-label="Aggiorna">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="p-3 rounded-lg border bg-primary/5">
            <div className="text-xs text-muted-foreground">Ricette totali</div>
            <div className="text-lg font-bold text-primary" data-testid="rx-stats-count">{data?.prescriptions || 0}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">Righe farmaco</div>
            <div className="text-lg font-semibold">{data?.total_drug_lines || 0}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">Farmaci unici</div>
            <div className="text-lg font-semibold">{data?.unique_drugs || 0}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">Top principio</div>
            <div className="text-lg font-semibold truncate" title={chartData[0]?.name || '—'}>
              {chartData[0]?.name || '—'}
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : chartData.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Nessuna prescrizione nel periodo selezionato.
          </div>
        ) : (
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" fontSize={12} />
                <YAxis type="category" dataKey="label" fontSize={11} width={180} />
                <Tooltip
                  formatter={(value, name, props) => [`${value} volte (${props.payload.pct}%)`, 'Prescritto']}
                  labelFormatter={(l) => l.replace(/^\d+\.\s*/, '')}
                />
                <Bar dataKey="count" fill={primary} radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={primary} fillOpacity={1 - index * 0.06} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RxStatsChart;
